<script setup>
import { ref, onMounted, computed } from 'vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getAssets } from '@/lib/asset';
import { ChevronRight, Grid3x3 } from 'lucide-vue-next';
import Asset from '@/components/create/asset/Asset.vue';
import { $t } from '@/i18n';

const props = defineProps({
  type: {
    type: String,
    default: 'material',
  },
});

const assetsState = ref({
  assets: [],
  loading: false,
  error: null,
  hasMore: true,
  page: 1,
  pageSize: 16,
});

const emit = defineEmits(['pick']);

defineExpose({
  flush,
});

const isOpen = ref(false);

// 计算显示的预览资源
const previewAssets = computed(() => assetsState.value.assets.slice(0, 4));
const hasAssets = computed(() => assetsState.value.assets.length > 0);

function pickAsset(asset) {
  emit('pick', asset);
  isOpen.value = false;
}

function flush() {
  fetchAssets({ reset: true });
}

onMounted(() => {
  fetchAssets();
});

async function fetchAssets({ reset = false } = {}) {
  if (assetsState.value.loading) return;
  assetsState.value.loading = true;
  assetsState.value.error = null;

  try {
    const page = reset ? 1 : assetsState.value.page;
    const response = await getAssets({
      limit: assetsState.value.pageSize,
      skip: (page - 1) * assetsState.value.pageSize,
      source: props.type,
    });

    if (reset) {
      assetsState.value.assets = response;
    } else {
      assetsState.value.assets = [...assetsState.value.assets, ...response];
    }

    assetsState.value.hasMore = response.length === assetsState.value.pageSize;
    assetsState.value.page = page + 1;
  } catch (err) {
    console.error('Failed to fetch assets:', err);
    assetsState.value.error = $t('createAssetPicker.error');
    if (reset) assetsState.value.assets = [];
  } finally {
    assetsState.value.loading = false;
  }
}

function handleOpenChange(open) {
  isOpen.value = open;
  if (open && assetsState.value.assets.length === 0 && !assetsState.value.loading) {
    assetsState.value.page = 1;
    fetchAssets({ reset: true });
  }
}

// 滚动加载更多
async function handleScroll(e) {
  const el = e.target;
  if (
    assetsState.value.hasMore &&
    !assetsState.value.loading &&
    el.scrollHeight - el.scrollTop - el.clientHeight < 80
  ) {
    await fetchAssets();
  }
}
</script>

<template>
  <div class="space-y-4">
    <!-- 预览网格 -->
    <div v-if="hasAssets" class="space-y-3">
      <div class="flex items-center justify-between">
        <h4 class="text-xs font-medium text-black/70 flex items-center gap-2">
          {{ $t('createAssetPicker.historyTitle') }}
        </h4>
        <Popover :open="isOpen" @update:open="handleOpenChange">
          <PopoverTrigger as-child>
            <button
              class="flex items-center gap-1 text-xs text-black/70 hover:text-black/90 font-medium transition-colors px-2 py-1 rounded-md hover:bg-black/5 focus:outline-none"
            >
              {{ $t('createAssetPicker.viewAll') }}
              <ChevronRight class="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            class="w-[420px] h-[480px] overflow-hidden rounded-xl shadow-xl bg-white border border-black/10/80 p-0"
            side="bottom"
            align="end"
          >
            <div class="p-4 border-b border-black/5 bg-black/5/50">
              <h3 class="font-semibold text-black/90 text-sm">
                {{ $t('createAssetPicker.materialLibrary') }}
              </h3>
              <p class="text-xs text-black/50 mt-1">
                {{ $t('createAssetPicker.materialLibraryDesc') }}
              </p>
            </div>

            <div class="overflow-y-auto max-h-[400px] custom-scrollbar" @scroll="handleScroll">
              <!-- 加载状态 -->
              <div
                v-if="assetsState.loading && assetsState.assets.length === 0"
                class="flex flex-col items-center justify-center py-12 text-black/40"
              >
                <div
                  class="w-8 h-8 border-2 border-black/20 border-t-blue-500 rounded-full animate-spin mb-3"
                ></div>
                <span class="text-sm">{{ $t('createAssetPicker.loading') }}</span>
              </div>

              <!-- 错误状态 -->
              <div
                v-else-if="assetsState.error && assetsState.assets.length === 0"
                class="flex flex-col items-center justify-center py-12 text-red-400"
              >
                <div class="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mb-3">
                  <span class="text-red-500">!</span>
                </div>
                <span class="text-sm">{{ assetsState.error }}</span>
              </div>

              <!-- 空状态 -->
              <div
                v-else-if="assetsState.assets.length === 0"
                class="flex flex-col items-center justify-center py-12 text-black/40"
              >
                <Grid3x3 class="w-8 h-8 mb-3" />
                <span class="text-sm">{{ $t('createAssetPicker.noMaterials') }}</span>
              </div>

              <!-- 素材网格 -->
              <div v-else class="p-4">
                <div class="grid grid-cols-4 gap-3">
                  <Asset
                    v-for="asset in assetsState.assets"
                    :key="asset.id"
                    :asset="asset"
                    @click="pickAsset(asset)"
                  />
                </div>

                <!-- 加载更多指示器 -->
                <div
                  v-if="assetsState.loading && assetsState.assets.length > 0"
                  class="flex justify-center py-4"
                >
                  <div
                    class="w-5 h-5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"
                  ></div>
                </div>

                <!-- 没有更多内容 -->
                <div
                  v-if="
                    !assetsState.loading && !assetsState.hasMore && assetsState.assets.length > 0
                  "
                  class="text-center text-xs text-black/40 py-4 border-t border-black/5 mt-4"
                >
                  {{ $t('createAssetPicker.showingAll') }}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <!-- 预览网格 -->
      <div class="grid grid-cols-4 gap-3 px-1">
        <Asset
          v-for="asset in previewAssets"
          :key="asset.id"
          :asset="asset"
          @click="pickAsset(asset)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
